/**
 * The invite page — the one a cold prospect actually receives.
 *
 * Visual system: "Work Order" (fnd-hi6.1) — the trades' own paperwork. Paper
 * and ink, one accent (marking-paint orange), hairline rules, Bricolage
 * Grotesque over Instrument Sans with IBM Plex Mono for every label. The tokens
 * and component classes live in `invite/wo.css`, scoped under `.wo-page`, and
 * the reusable pieces in `invite/wo.tsx`; the dashboard keeps its own system.
 *
 * Structural commitments, unchanged by the redesign:
 *   1. The website is the pitch. It is the first and biggest thing on screen,
 *      scrollable in place, so the visitor can answer "is this mine?" without
 *      leaving for another domain.
 *   2. A job sheet states the terms in the trades' own idiom — what it is, what
 *      it was built from, and that today's total is $0.00.
 *   3. A sticky action bar follows them down the page, so the claim is never
 *      more than one tap away no matter how far they read.
 *   4. There is a rung between "looked" and "paid": ask for a change. It costs
 *      the visitor nothing, opens a conversation, and someone who shapes the
 *      site starts treating it as theirs.
 *   5. Prices come from `lib/pricing` only, as invoice line items with a
 *      "due today $0.00" total. Nothing on this page is a hardcoded dollar
 *      figure, an invented statistic, a review count or a "since 20XX".
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CARD_FEATURES,
  FOUNDING_PRICING_BADGE,
  PUBLIC_PLANS,
  type PlanId,
} from '../lib/pricing';
import { saveTaskState } from '../lib/setupTasks';
import {
  CONTACT_EMAIL,
  invoiceMailtoHref,
  invoiceSmsHref,
  ONLINE_CHECKOUT_ENABLED,
  type InvoiceRequest,
} from '../lib/claimMode';
import { setAccessToken } from '../services/tokenStore';
import { personalFirstName } from '../lib/contactName';
import ScrollPreviewImage from './ScrollPreviewImage';
import ClaimWizard, { type ClaimSubmission } from './invite/ClaimWizard';
import ChangeChat from './invite/ChangeChat';
import DashboardPreview from './invite/DashboardPreview';
import MarketingFooter from './invite/MarketingFooter';
import WelcomePackSection from './invite/WelcomePackSection';
import {
  DEMO_INVITES,
  type InviteInfo,
  type InviteStatus,
  isDemoToken,
  normalizeExternalUrl,
  PLAN_COPY,
  tradeAudience,
  tradeName,
  usableInviteEmail,
} from './invite/kit';
import {
  ArrowIcon,
  BrowserFrame,
  CheckIcon,
  Eyebrow,
  ExternalIcon,
  FactList,
  JobSheet,
  LedgerRow,
  Pill,
  Section,
  SheetTotal,
  WoButton,
  WoLinkButton,
  WorkOrderShell,
  Wrap,
} from './invite/wo';

const SUPPORT_EMAIL = CONTACT_EMAIL;

/** Cards stay level: the wizard is where the full feature matrix is read. */
const CARD_FEATURE_LIMIT = 5;

function formatDate(iso?: string | null) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * A plausible `.com` for the DOMAIN ledger row — "Noble Roots Landscaping" →
 * "noblerootslandscaping.com". Illustrative only (the actual purchase is
 * whatever the owner confirms at claim time, hence "or the one you prefer"
 * next to it): strip everything but letters and digits and lowercase what's
 * left, the way a person reading the name off a sign would type it into a
 * registrar with no hyphens.
 */
function guessDotComDomain(businessName: string): string {
  const bare = businessName.toLowerCase().replace(/[^a-z0-9]/g, '');
  return bare ? `${bare}.com` : 'yourbusiness.com';
}

function Brand() {
  return (
    <a className="wo-brand" href="https://foundlio.com">
      <img src="/foundlio-on-light.svg" alt="" aria-hidden="true" />
      Foundlio
    </a>
  );
}

/**
 * Contact mode (fnd-3uws): while online checkout is off, "keep this website"
 * is a text to the sender for an invoice. Email is the fallback. Rendered only
 * when ONLINE_CHECKOUT_ENABLED is false; the Stripe path is not reachable then.
 */
function InvoiceCta({ request }: { request: InvoiceRequest }) {
  return (
    <div className="wo-invoice">
      <p className="wo-invoice-copy">
        Want this site? Text me and I&apos;ll send you an invoice for ${request.monthly}/mo. Nothing is charged online.
      </p>
      <WoLinkButton href={invoiceSmsHref(request)}>
        Text Mike for an invoice
        <ArrowIcon />
      </WoLinkButton>
      <a className="wo-invoice-alt" href={invoiceMailtoHref(request)}>
        or email an invoice request
      </a>
    </div>
  );
}

function StatePage({ status }: { status: 'invalid' | 'expired' | 'used' }) {
  const copy = {
    invalid: {
      eyebrow: 'Invitation not found',
      title: 'This link is not valid.',
      body: 'The invitation may have been replaced or the URL may be incomplete.',
      action: 'Contact Foundlio',
      href: `mailto:${SUPPORT_EMAIL}`,
    },
    expired: {
      eyebrow: 'Invitation expired',
      title: 'This preview is no longer reserved.',
      body: 'Ask for a fresh invitation and we will reconnect it to the latest version of your website.',
      action: 'Request a new link',
      href: `mailto:${SUPPORT_EMAIL}`,
    },
    used: {
      eyebrow: 'Already activated',
      title: 'This website has already been claimed.',
      body: 'Sign in with the account created from this invitation.',
      action: 'Go to login',
      href: '/login',
    },
  }[status];

  return (
    <WorkOrderShell className="wo-state">
      <div className="wo-card">
        <Eyebrow>{copy.eyebrow}</Eyebrow>
        <h1 style={{ marginTop: 18, fontSize: '2.2rem' }}>{copy.title}</h1>
        <p className="wo-lede" style={{ marginTop: 16 }}>
          {copy.body}
        </p>
        <WoLinkButton href={copy.href} style={{ marginTop: 28 }}>
          {copy.action}
          <ArrowIcon />
        </WoLinkButton>
      </div>
    </WorkOrderShell>
  );
}

interface InvitePageProps {
  /**
   * fnd-k1d.4: the /claim route. The invite token is no longer in the URL —
   * `GET /api/claim/:slug` leaves a short-lived HttpOnly handoff cookie and
   * redirects here tokenless, so every call goes to the `/api/invite/me/*`
   * mirror of the token endpoints and the browser never holds the token.
   * Everything below this line is identical between the two modes.
   */
  claimMode?: boolean;
}

const InvitePage: React.FC<InvitePageProps> = ({ claimMode = false }) => {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<InviteStatus>('loading');
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanId>('professional');
  const [planTouched, setPlanTouched] = useState(false);
  const [error, setError] = useState('');
  const [usedGooglePrototype, setUsedGooglePrototype] = useState(false);
  const [barVisible, setBarVisible] = useState(false);
  /**
   * The claim confirmation code step (fnd-k1d.4). Holding the invite link is
   * no longer enough to set a password or move the account email — the server
   * emails a code to the address on file and refuses activation without it.
   */
  const [pendingClaim, setPendingClaim] = useState<ClaimSubmission | null>(null);
  const [codeSentTo, setCodeSentTo] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');

  const heroSentinel = useRef<HTMLDivElement | null>(null);
  const isDemo = !claimMode && isDemoToken(token);
  /** Token endpoints, or their cookie-authenticated mirrors. */
  const inviteBase = claimMode ? '/api/invite/me' : `/api/invite/${token}`;

  useEffect(() => {
    let active = true;

    const fetchInvite = async () => {
      if (!claimMode && !token) {
        setStatus('invalid');
        return;
      }

      if (!claimMode && import.meta.env.DEV && token && isDemoToken(token)) {
        setInviteInfo(DEMO_INVITES[token]);
        setStatus('preview');
        return;
      }

      try {
        const response = await axios.get(inviteBase);
        if (!active) return;
        setInviteInfo(response.data.data as InviteInfo);
        setStatus('preview');
      } catch (requestError) {
        if (!active) return;
        if (axios.isAxiosError(requestError)) {
          setStatus(
            requestError.response?.status === 410 ? 'expired' : 'invalid'
          );
        } else {
          setStatus('invalid');
        }
      }
    };

    fetchInvite();
    return () => {
      active = false;
    };
  }, [token, claimMode, inviteBase]);

  /**
   * The tab should say whose website this is, not "Client Portal". Social
   * unfurls are handled server-side (backend routes/invitePage.ts injects OG
   * meta into the shell) — this is for the human who actually opens the tab.
   */
  useEffect(() => {
    const name = inviteInfo?.website?.businessName || inviteInfo?.businessName;
    document.title = name
      ? `${name} — Your New Website | Foundlio`
      : 'Your New Website | Foundlio';
    return () => {
      document.title = 'Foundlio — Your Website, Handled';
    };
  }, [inviteInfo]);

  /**
   * The action bar appears once the hero — which carries its own CTA — is
   * behind them, and not before. A passive scroll read is used rather than an
   * IntersectionObserver: `isIntersecting` alone cannot tell "not scrolled to
   * yet" from "scrolled past", and the sign of the sentinel's offset can.
   */
  useEffect(() => {
    if (status !== 'preview') return;
    const sync = () => {
      const node = heroSentinel.current;
      if (node) setBarVisible(node.getBoundingClientRect().top < 0);
    };
    sync();
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [status]);

  const website = inviteInfo?.website;
  const businessName =
    website?.businessName || inviteInfo?.businessName || 'Your business';
  /**
   * A real person's first name, or `''`. `inviteInfo.name` is the *business*
   * name — the invite pipeline has always sent it as the user's display name —
   * so it is only a fallback here, and one that `personalFirstName` throws out
   * unless it genuinely reads as a person. The copy below has a written
   * variant for having no name; a guessed one has none of its honesty.
   */
  const rawContactName = website?.contactName || inviteInfo?.name || '';
  const firstName = personalFirstName(rawContactName, businessName);
  /**
   * Only prefill the claim form's "Your name" once the gate above has accepted
   * it. Prefilled with the company name it was worse than empty: the field
   * looked answered, so people submitted "Noble Roots Landscaping" as the
   * contact and had to be asked again later.
   */
  const contactFullName = firstName ? rawContactName.trim() : '';
  /**
   * "Rebuild" framing is only right when they own a site to rebuild. An
   * explicit `no_site` therefore wins over a stray `oldSiteUrl` — which, for a
   * business whose Maps profile points at a Yahoo or Facebook listing, is a
   * page they neither own nor can edit.
   */
  const isWeakSite =
    website?.siteCategory === 'weak_site' ||
    (website?.siteCategory !== 'no_site' && Boolean(website?.oldSiteUrl));
  const serviceTrade = tradeName(website?.trade);
  const city = website?.city;
  /** "roofers in Madison" — who these get built for, when we cannot say "you". */
  const audience = city
    ? `${tradeAudience(website?.trade)} in ${city}`
    : tradeAudience(website?.trade);
  const oldSiteHref = normalizeExternalUrl(website?.oldSiteUrl);
  const chosenPlan =
    PUBLIC_PLANS.find(plan => plan.id === selectedPlan) ?? PUBLIC_PLANS[0];
  /** What the contact-mode text and email ask for (fnd-3uws). */
  const invoiceRequest: InvoiceRequest = {
    businessName,
    siteUrl:
      website?.previewUrl ||
      (website?.domain ? `https://${website.domain}` : undefined),
    planName: chosenPlan.name,
    monthly: chosenPlan.monthly,
  };

  const screenshotSrc = website?.screenshotUrl || null;
  const domain = website?.domain || 'your-business.foundlio.com';
  /**
   * The DOMAIN ledger row: a plausible `.com` guessed from the business
   * name, illustrating the gift rather than promising this exact string —
   * "or the one you prefer" next to it says so. We buy and register whatever
   * `.com` the owner actually confirms at claim time.
   */
  const giftDomain = guessDotComDomain(businessName);
  const reservedUntil = formatDate(inviteInfo?.expiresAt);
  /**
   * The site's slug, taken from its preview domain (`<slug>.foundlio.com`). It
   * is public, unlike the invite token, so it is what travels on the footer
   * links that let foundlio.com recognise this visitor later.
   */
  const siteSlug = website?.domain?.split('.')[0] || undefined;
  /**
   * The live site link, carrying the same `?ref=<slug>` the marketing footer
   * appends (`MarketingFooter.tsx`) — it repairs the `.foundlio.com`
   * preview-recall cookie for a visitor who follows this link straight to
   * their deployed site (private window, cookie already cleared), which is
   * also what gates that site's own welcome-pack pitch to invite visitors.
   */
  const previewUrlWithRef = (url: string) =>
    siteSlug ? `${url}${url.includes('?') ? '&' : '?'}ref=${encodeURIComponent(siteSlug)}` : url;

  function openClaim(planId?: PlanId) {
    // Contact mode (fnd-3uws): the wizard ends in Stripe, so it stays shut.
    if (!ONLINE_CHECKOUT_ENABLED) return;
    if (planId) {
      setSelectedPlan(planId);
      setPlanTouched(true);
    }
    setError('');
    setStatus('activate');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function continueWithGooglePrototype() {
    if (!isDemo || !import.meta.env.DEV) return;
    setUsedGooglePrototype(true);
    setStatus('activating');
    window.setTimeout(() => setStatus('success'), 650);
  }

  /**
   * Step one of activation (fnd-k1d.4): ask the server to email a
   * confirmation code, then hold the form data until they type it back.
   *
   * The address the code goes to is the server's decision, not this form's —
   * when the invite already has a real address on file it always wins, so
   * someone holding a scraped invite link cannot redirect the proof to a
   * mailbox they own. `sentTo` comes back masked.
   */
  async function activate(data: ClaimSubmission) {
    setError('');
    setSelectedPlan(data.plan);
    setStatus('activating');

    if (isDemo && import.meta.env.DEV) {
      window.setTimeout(() => setStatus('success'), 650);
      return;
    }

    try {
      const response = await axios.post(`${inviteBase}/request-code`, {
        email: data.email,
      });
      setPendingClaim(data);
      setCodeSentTo(response.data?.data?.sentTo || '');
      setConfirmationCode('');
      setStatus('confirm');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (requestError) {
      setStatus('activate');
      if (axios.isAxiosError(requestError)) {
        if (requestError.response?.status === 410) {
          setStatus('expired');
        } else if (requestError.response?.status === 409) {
          setStatus('used');
        } else {
          setError(
            requestError.response?.data?.error ||
              'Could not send the confirmation code. Please try again.'
          );
        }
      } else {
        setError('Could not send the confirmation code. Please try again.');
      }
    }
  }

  /** Step two: activate, now with the emailed code as the possession proof. */
  async function confirmAndActivate() {
    const data = pendingClaim;
    if (!data) return;

    setError('');
    setStatus('activating');

    try {
      const response = await axios.post(`${inviteBase}/activate`, {
        email: data.email,
        password: data.password,
        plan: data.plan,
        // They just told us who they are — store it, so the dashboard stops
        // greeting them by their company name.
        contactName: data.contactName,
        confirmationCode: confirmationCode.trim(),
      });
      const { token: authToken, refreshToken } = response.data.data;
      if (refreshToken) {
        // /invite/:token/activate hands back a refresh token in the body
        // instead of a cookie (it's a brand-new account, not a login), so
        // trade it in immediately for the httpOnly cookie /api/auth/refresh-token
        // sets — the only place a refresh token is allowed to live. Without
        // this, a reload right after activating has no way back in, now that
        // the access token itself is memory-only (fnd-lly.3).
        try {
          await axios.post('/api/auth/refresh-token', { refreshToken });
        } catch {
          // Best-effort — the access token set below still works until it expires.
        }
      }
      setAccessToken(authToken);
      axios.defaults.headers.common.Authorization = `Bearer ${authToken}`;

      // Hand the details forward so /welcome does not ask for them twice.
      saveTaskState('confirm-details', {
        data: {
          businessName,
          contactName: data.contactName,
          phone: data.phone,
          contactPreference: data.contactPreference,
        },
      });

      // Stripe redirect: only when online checkout is on (fnd-3uws).
      if (ONLINE_CHECKOUT_ENABLED) {
        try {
          const checkout = await axios.post('/api/payment/checkout-session', {
            planType: data.plan,
            returnTo: 'setup',
          });
          const checkoutUrl = checkout.data?.data?.url;
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            return;
          }
        } catch (checkoutError) {
          console.error(
            'Could not start checkout, continuing to dashboard:',
            checkoutError
          );
        }
      }

      setStatus('success');
      window.setTimeout(() => {
        window.location.href = '/welcome';
      }, 1800);
    } catch (requestError) {
      setStatus('confirm');
      if (axios.isAxiosError(requestError)) {
        if (requestError.response?.status === 410) {
          setStatus('expired');
        } else if (requestError.response?.status === 409) {
          setStatus('used');
        } else {
          setError(
            requestError.response?.data?.error ||
              'Could not activate the account. Please try again.'
          );
        }
      } else {
        setError('An unexpected error occurred.');
      }
    }
  }

  if (status === 'loading') {
    return (
      <WorkOrderShell className="wo-state">
        <div style={{ textAlign: 'center' }}>
          <div aria-hidden="true" className="wo-spinner" />
          <Eyebrow className="wo-loading-label">Opening your website</Eyebrow>
        </div>
      </WorkOrderShell>
    );
  }

  if (status === 'invalid' || status === 'expired' || status === 'used') {
    return <StatePage status={status} />;
  }

  if (status === 'success') {
    return (
      <WorkOrderShell className="wo-state">
        <div className="wo-card" style={{ textAlign: 'center' }}>
          <Eyebrow>Website claimed</Eyebrow>
          <h1 style={{ marginTop: 18, fontSize: '2.6rem' }}>It&apos;s yours.</h1>
          <p className="wo-lede" style={{ marginTop: 16, marginInline: 'auto' }}>
            {isDemo
              ? usedGooglePrototype
                ? 'The Google sign-in prototype is complete. No Google account, Foundlio account or payment was created.'
                : 'The email sign-up prototype is complete. No account or payment was created.'
              : 'Your account is active. We are taking you to the next step now.'}
          </p>
          {isDemo ? (
            <WoLinkButton
              href={`/invite/${isWeakSite ? 'demo-weak-site' : 'demo-no-site'}`}
              style={{ marginTop: 28 }}
            >
              Restart demo
              <ArrowIcon />
            </WoLinkButton>
          ) : (
            <Eyebrow className="wo-loading-label">
              Redirecting to your dashboard…
            </Eyebrow>
          )}
        </div>
      </WorkOrderShell>
    );
  }

  if (status === 'confirm') {
    return (
      <WorkOrderShell className="wo-state">
        <form
          className="wo-card"
          style={{ maxWidth: '28rem' }}
          onSubmit={event => {
            event.preventDefault();
            void confirmAndActivate();
          }}
        >
          <Eyebrow>One last check</Eyebrow>
          <h1 style={{ marginTop: 18, fontSize: '2rem' }}>Enter your code</h1>
          <p className="wo-lede" style={{ marginTop: 14, fontSize: 16 }}>
            We sent a six-digit code to{' '}
            <span style={{ color: 'var(--ink)', fontWeight: 500 }}>
              {codeSentTo || 'your email'}
            </span>
            . It confirms the mailbox is yours before we create the account.
          </p>

          <label
            className="wo-eyebrow"
            style={{ display: 'block', marginTop: 26, marginBottom: 8 }}
            htmlFor="claim-confirmation-code"
          >
            Confirmation code
          </label>
          <input
            id="claim-confirmation-code"
            name="confirmationCode"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={confirmationCode}
            onChange={event => setConfirmationCode(event.target.value)}
            className="wo-input wo-mono"
            style={{
              textAlign: 'center',
              fontSize: 24,
              letterSpacing: '0.4em',
              background: '#fff',
            }}
          />

          {error && (
            <p style={{ marginTop: 16, color: '#a8271b' }} role="alert">
              {error}
            </p>
          )}

          <WoButton
            type="submit"
            disabled={confirmationCode.trim().length < 6}
            style={{ marginTop: 24, width: '100%' }}
          >
            Claim my website
            <ArrowIcon />
          </WoButton>

          <button
            type="button"
            onClick={() => {
              if (pendingClaim) void activate(pendingClaim);
            }}
            style={{
              marginTop: 16,
              width: '100%',
              background: 'none',
              border: 0,
              cursor: 'pointer',
              font: 'inherit',
              fontSize: 14,
              color: 'var(--ink-2)',
              textDecoration: 'underline',
            }}
          >
            Send it again
          </button>
        </form>
      </WorkOrderShell>
    );
  }

  if (status === 'activate' || status === 'activating') {
    return (
      <WorkOrderShell>
        <header className="wo-hdr">
          <Wrap>
            <Brand />
          </Wrap>
        </header>
        <ClaimWizard
          businessName={businessName}
          inviteEmail={usableInviteEmail(inviteInfo?.email)}
          inviteName={contactFullName}
          businessPhone={website?.businessPhone ?? ''}
          initialPlan={selectedPlan}
          planPreselected={planTouched}
          busy={status === 'activating'}
          error={error}
          isDemo={isDemo}
          onExit={() => setStatus('preview')}
          onSubmit={activate}
          onGooglePrototype={isDemo ? continueWithGooglePrototype : undefined}
        />
      </WorkOrderShell>
    );
  }

  return (
    <WorkOrderShell>
      {isDemo && (
        <div className="wo-demo">
          <Wrap>
            <span>
              <b>Design prototype</b> · real invite data is not used
            </span>
            <span style={{ display: 'flex', gap: 18 }}>
              <a
                href="/invite/demo-no-site"
                style={{ fontWeight: !isWeakSite ? 600 : 400 }}
              >
                No website
              </a>
              <a
                href="/invite/demo-weak-site"
                style={{ fontWeight: isWeakSite ? 600 : 400 }}
              >
                Outdated website
              </a>
            </span>
          </Wrap>
        </div>
      )}

      <header className="wo-hdr">
        <Wrap>
          <Brand />
          <span className="wo-prep">
            Prepared for <b>{businessName}</b>
            {city ? ` · ${city}` : ''}
            {reservedUntil ? ` · reserved until ${reservedUntil}` : ''}
          </span>
          {ONLINE_CHECKOUT_ENABLED ? (
            <WoButton type="button" small onClick={() => openClaim(selectedPlan)}>
              Keep this website
              <ArrowIcon />
            </WoButton>
          ) : (
            <WoLinkButton small href={invoiceSmsHref(invoiceRequest)}>
              Text Mike
              <ArrowIcon />
            </WoLinkButton>
          )}
        </Wrap>
      </header>

      <main>
        {/* ---------- The job sheet, and the promise ---------- */}
        <section className="wo-wrap wo-hero">
          <div>
            <Eyebrow className="wo-rv">
              {[city, serviceTrade].filter(Boolean).join(' · ') ||
                'Your private preview'}
            </Eyebrow>
            <h1 className="wo-rv" style={{ marginTop: 22 }}>
              {businessName},{' '}
              <span className="wo-dim">
                {isWeakSite ? 'here is the rebuild.' : 'here is your website.'}
              </span>
            </h1>
            <p className="wo-lede wo-rv" style={{ marginTop: 24 }}>
              It is already built and online. Scroll it below, or open the real
              thing. Nothing is published on your domain and you owe nothing for
              looking.
            </p>
            <div className="wo-hero-cta wo-rv">
              {ONLINE_CHECKOUT_ENABLED ? (
                <WoButton type="button" onClick={() => openClaim()}>
                  Keep this website — ${chosenPlan.monthly}/mo
                  <ArrowIcon />
                </WoButton>
              ) : (
                <InvoiceCta request={invoiceRequest} />
              )}
              {website?.previewUrl && (
                <WoLinkButton
                  tone="ghost"
                  href={previewUrlWithRef(website.previewUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open the real thing
                  <ExternalIcon />
                </WoLinkButton>
              )}
            </div>
            <p className="wo-hero-note wo-rv">
              This link is private to you.
              {reservedUntil
                ? ` It stops working on ${reservedUntil} unless you keep the site or ask for more time.`
                : ' Ask for more time whenever you need it.'}
            </p>
          </div>

          <JobSheet
            className="wo-rv"
            reference={siteSlug}
            footer={
              <>
                <span>
                  {reservedUntil ? (
                    <>
                      Reserved for you until <b>{reservedUntil}</b>.
                    </>
                  ) : (
                    <>Reserved for you.</>
                  )}
                </span>
                <span className="wo-sig">
                  <span />
                  Approved by
                </span>
              </>
            }
          >
            <LedgerRow label="Business">
              {businessName}
              {city && (
                <>
                  <br />
                  <span className="wo-dim" style={{ fontSize: 13 }}>
                    {city}
                  </span>
                </>
              )}
            </LedgerRow>
            <LedgerRow label="Built from">
              Your public Google listing — the services, the area you cover and
              what your reviews say about the work.
            </LedgerRow>
            <LedgerRow label="Trade">
              <div className="wo-tags">
                <span>{serviceTrade}</span>
                {city && <span>{city}</span>}
                <span>{isWeakSite ? 'rebuild' : 'first website'}</span>
              </div>
            </LedgerRow>
            <LedgerRow label="Status">
              <Pill ok>Live · unpublished</Pill>
            </LedgerRow>
            <LedgerRow label="Domain">
              <b>{giftDomain}</b>
              <br />
              <span className="wo-dim" style={{ fontSize: 13 }}>
                we buy it, registered in your name — or the one you prefer
              </span>
            </LedgerRow>
            <LedgerRow label="Due today" stamp="Not published">
              <SheetTotal amount="$0.00" note="nothing until you say yes" />
            </LedgerRow>
          </JobSheet>
        </section>

        {/* ---------- The website itself ---------- */}
        <section className="wo-wrap" style={{ paddingBottom: 72 }}>
          <div className="wo-pv-head">
            <Eyebrow>
              Your website · <b>{domain}</b>
            </Eyebrow>
            <span className="wo-eyebrow">
              {screenshotSrc
                ? 'Hover or tap inside the window to scroll it'
                : 'Open the live version to review the whole site'}
            </span>
          </div>
          <BrowserFrame url={domain} badge={<Pill ok>Live</Pill>}>
            {screenshotSrc ? (
              <ScrollPreviewImage
                src={screenshotSrc}
                alt={`${businessName} website preview`}
                className="h-full w-full"
              />
            ) : (
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 32,
                  textAlign: 'center',
                }}
              >
                <div
                  className="wo-display"
                  style={{ fontSize: 20, fontWeight: 600 }}
                >
                  Your website is ready
                </div>
                <p
                  className="wo-muted"
                  style={{ marginTop: 8, maxWidth: '22rem', fontSize: 15 }}
                >
                  Open the live version to review the complete site.
                </p>
              </div>
            )}
          </BrowserFrame>
          <div ref={heroSentinel} aria-hidden className="h-px" />
        </section>

        {/* ---------- What they are looking at ---------- */}
        <Section>
          <Eyebrow>What you are looking at</Eyebrow>
          <h2 style={{ marginTop: 14, maxWidth: '24ch' }}>
            {isWeakSite
              ? 'Same business. A tool that keeps up with it.'
              : 'Somewhere useful to send the people who already find you.'}
          </h2>
          <p className="wo-lede" style={{ marginTop: 20 }}>
            {isWeakSite
              ? `The point is not that the old site is bad. It is that ${businessName} should be easy to trust, read and call from a phone.`
              : `A Maps listing gets ${businessName} discovered. A website is what turns that into a call.`}
          </p>
          {isWeakSite && oldSiteHref && (
            <p style={{ marginTop: 16 }}>
              <a
                href={oldSiteHref}
                target="_blank"
                rel="noopener noreferrer"
                className="wo-muted"
                style={{ fontSize: 15, textDecoration: 'underline' }}
              >
                Open the current site ↗
              </a>
            </p>
          )}

          <div className="wo-what">
            {[
              {
                title: 'Written for your trade',
                body: `Service pages aimed at ${serviceTrade} customers${
                  city ? ` in ${city}` : ''
                }, not filler text.`,
              },
              {
                title: 'Built for a thumb',
                body: 'Tap-to-call and a request form on every screen, because most of your traffic is on a phone with a problem in progress.',
              },
              {
                title: 'Your proof, up front',
                body: 'Services, service area, reviews and credentials in the order people actually look for them.',
              },
              {
                title: 'Yours to keep',
                body: 'We buy the .com and register it in your name, and the site files leave with you if you ever go.',
              },
            ].map((item, index) => (
              <div key={item.title}>
                <p className="wo-no">{String(index + 1).padStart(2, '0')}</p>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* ---------- The middle rung ---------- */}
        <Section>
          <div className="wo-two">
            <div>
              <Eyebrow>Not quite right?</Eyebrow>
              <h2 style={{ marginTop: 14 }}>
                Tell me what to change and I&apos;ll change it.
              </h2>
              <p className="wo-lede" style={{ marginTop: 22 }}>
                Wrong hours, a service you don&apos;t offer, a photo you hate, a
                headline that isn&apos;t how you talk. Say so and I&apos;ll fix
                it before you decide anything. No cost, no commitment, no
                obligation to keep it afterwards.
              </p>
              <p
                className="wo-dim"
                style={{ marginTop: 18, fontSize: 15, maxWidth: '32rem' }}
              >
                This is not a contact form — it is the same conversation you
                keep after you claim the site. Asking for changes this way is
                what the monthly plan is: you write, I edit.
              </p>
            </div>

            <ChangeChat
              token={token}
              apiBase={isDemo ? undefined : inviteBase}
              businessName={businessName}
              supportEmail={SUPPORT_EMAIL}
              isDemo={isDemo}
            />
          </div>
        </Section>

        {/* ---------- "Your dashboard" preview (fnd-hi6.5) ---------- */}
        <DashboardPreview token={token} businessName={businessName} trade={website?.trade} />

        {/* ---------- Price, as invoice line items ---------- */}
        <Section id="keep">
          <div style={{ maxWidth: '40rem' }}>
            <Eyebrow>If you keep it</Eyebrow>
            <h2 style={{ marginTop: 14 }}>
              The website is done. You are choosing how much monthly work goes
              into it.
            </h2>
            {/* The one-sentence answer to "so I'm renting a page?" — the full
                argument (and the whole feature matrix) lives in the wizard,
                where the plan is actually chosen. */}
            <p className="wo-lede" style={{ marginTop: 20 }}>
              What you pay monthly is the person and the upkeep — edits when you
              write, hosting, and the work of being found — not rent on a
              finished page.
            </p>
          </div>

          <div className="wo-plans" role="group" aria-label="Plans">
            {PUBLIC_PLANS.map(plan => {
              const selected = selectedPlan === plan.id;
              const card = CARD_FEATURES[plan.id];
              return (
                <button
                  key={plan.id}
                  type="button"
                  className="wo-plan"
                  aria-pressed={selected}
                  onClick={() => {
                    setSelectedPlan(plan.id);
                    setPlanTouched(true);
                  }}
                >
                  <div className="wo-fit">
                    <span>
                      {PLAN_COPY[plan.id].fit}
                      {plan.anchorMonthly ? ` · ${FOUNDING_PRICING_BADGE}` : ''}
                    </span>
                    <span className="wo-sel">
                      <CheckIcon /> Selected
                    </span>
                  </div>
                  <div className="wo-top">
                    <span className="wo-pn">{plan.name}</span>
                    <span className="wo-pp wo-num">
                      {plan.anchorMonthly && (
                        <span className="wo-was">${plan.anchorMonthly}</span>
                      )}
                      ${plan.monthly}
                      <small>/ MO</small>
                    </span>
                  </div>
                  <p className="wo-pd">{PLAN_COPY[plan.id].label}</p>
                  <ul>
                    {card.items.slice(0, CARD_FEATURE_LIMIT).map(item => (
                      <li key={item}>
                        <CheckIcon />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>

          <div className="wo-keep">
            {ONLINE_CHECKOUT_ENABLED ? (
              <WoButton type="button" onClick={() => openClaim(selectedPlan)}>
                Keep this website with {chosenPlan.name}
                <ArrowIcon />
              </WoButton>
            ) : (
              <InvoiceCta request={invoiceRequest} />
            )}
            <span className="wo-due">
              Setup <b>$0.00</b> · due today <b>$0.00</b> · .com domain on us ·
              plan can change later · cancel any month
            </span>
          </div>

          {/* The welcome pack, as a perk of the plan it ships with — part of
              the price chapter, not a chapter of its own. */}
          <WelcomePackSection
            token={token}
            apiBase={isDemo ? undefined : inviteBase}
            isDemo={isDemo}
            businessName={businessName}
            tradeName={serviceTrade}
            city={city}
            domain={website?.domain}
            owner={contactFullName}
          />
        </Section>

        {/* ---------- Who sent this ---------- */}
        <Section dark>
          <div className="wo-two">
            <div>
              <Eyebrow light>Why this arrived</Eyebrow>
              <h2 style={{ marginTop: 16 }}>
                A finished website is easier to judge than a sales pitch.
              </h2>
              <p style={{ marginTop: 18 }}>
                That is the whole business model: build the real thing first, so
                nobody has to buy a promise.
              </p>
              <FactList
                items={[
                  {
                    label: 'Found',
                    body: `${businessName} on Google Maps${city ? ` in ${city}` : ''}${
                      isWeakSite
                        ? ' — with a site that is hard to use on a phone'
                        : ' — with no website of its own'
                    }`,
                  },
                  {
                    label: 'Built',
                    body: 'From public details only: the listing, the reviews, the photos. Checked by hand before this link was sent.',
                  },
                  {
                    label: 'Not interested?',
                    body: 'Ignore this and the link expires. Nothing else happens — there is no follow-up sequence.',
                  },
                ]}
              />
            </div>

            {/* Two variants, because the honest thing to say depends on
                whether we know who is reading. With a real first name the line
                opens on them. Without one it opens by admitting nobody asked —
                which is the question a stranger actually has — and buys back
                the specificity with their trade and city. */}
            <div className="wo-letter">
              <p>
                Hi — I&apos;m Mikhail. I run Foundlio on my own: I wrote the
                software that builds these sites and I check every one before it
                goes out.
              </p>
              <p>
                {firstName ? (
                  <>{firstName}, I build these from public business details</>
                ) : (
                  <>
                    No one at {businessName} asked for this. I build websites
                    for {audience} from public business details
                  </>
                )}{' '}
                — the listing, the reviews, the photos — and send the result
                instead of a cold pitch. If it is useful, keep it. If it is not,
                ignore this and nothing happens.
              </p>
              <p>
                If it is wrong somewhere, use the change notes above and I will
                fix it. The domain is registered in your name.
              </p>
              <div className="wo-from">
                <div className="wo-av">M</div>
                <div>
                  <div className="wo-nm">Mikhail</div>
                  <div className="wo-rl">Founder · the person who replies</div>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </main>

      <MarketingFooter slug={siteSlug} supportEmail={SUPPORT_EMAIL} />

      {/* ---------- The bar that follows them down ---------- */}
      <div
        className="wo-bar"
        style={{ transform: barVisible ? 'translateY(0)' : 'translateY(100%)' }}
      >
        <Wrap>
          <div style={{ minWidth: 0 }}>
            <div className="wo-bar-name">{businessName}</div>
            <div className="wo-bar-sub">
              ${chosenPlan.monthly}/mo · $0 setup · cancel any time
            </div>
          </div>
          <div
            style={{ display: 'flex', gap: 8, flex: 'none', alignItems: 'center' }}
          >
            {website?.previewUrl && (
              <WoLinkButton
                tone="ghost"
                small
                className="wo-bar-open"
                href={previewUrlWithRef(website.previewUrl)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--on-dark)', borderColor: 'var(--dark-rule)' }}
              >
                Open site
                <ExternalIcon />
              </WoLinkButton>
            )}
            {ONLINE_CHECKOUT_ENABLED ? (
              <WoButton
                type="button"
                tone="light"
                small
                onClick={() => openClaim(selectedPlan)}
              >
                Keep this website
                <ArrowIcon />
              </WoButton>
            ) : (
              <WoLinkButton
                tone="light"
                small
                href={invoiceSmsHref(invoiceRequest)}
              >
                Text Mike
                <ArrowIcon />
              </WoLinkButton>
            )}
          </div>
        </Wrap>
      </div>
    </WorkOrderShell>
  );
};

export default InvitePage;
